import React from 'react';
import { Box, IconButton, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Close, AssignmentTurnedInOutlined, HowToRegOutlined, CheckCircle, Cancel } from '@material-ui/icons';
import Icon from '@mdi/react';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, convertFromRaw } from 'draft-js';
import { iconList } from '../../../common/icons/RoleIconList';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { getStatusColors, GoToButton } from '../../../common/cards/ExpandableCard';
import { TasksDialog } from '../user-profile/tasks/TasksDialog';

type RoleSidebarTask = { taskId: string; title: string };

export type RoleSidebarRole = {
  activityRoleId: string;
  name: string;
  icon: string | null;
  description: string | null;
  applicationMode: string;
  tasks: RoleSidebarTask[];
};

const APPLICATION_MODE_LABEL = new Map<string, string>([
  ['auto', 'Auto Approval'],
  ['tasks_auto', 'Complete Tasks'],
  ['tasks_manual', 'Tasks & Review'],
  ['approve_tasks_manual', 'Review, Tasks & Approval'],
]);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      [theme.breakpoints.down('lg')]: {
        marginLeft: -16,
        marginRight: -16,
      },
      padding: '16px 20px',
    },
    closeButton: {
      border: '1px solid',
      borderColor: theme.color.grey.neutralBrand200,
      borderRadius: '6px',
      backgroundColor: 'white',
      padding: '6px',
    },
    header: {
      borderBottom: `1px solid ${theme.color.grey.border}`,
      paddingBottom: '16px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
    },
    titleText: {
      marginLeft: 16,
    },
    iconBox: {
      width: 36,
      height: 36,
      borderRadius: 6,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
    title: {
      color: theme.color.grey.neutral500,
      fontWeight: 800,
      fontSize: '1.25rem',
      letterSpacing: '-0.02em',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      marginTop: '24px',
      marginBottom: '10px',
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: theme.color.grey.neutral400,
    },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      color: theme.color.grey.neutral200,
      marginTop: '14px',
      flexWrap: 'wrap',
    },
    metaCell: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: 16,
      '& > * + *': {
        marginLeft: 8,
      },
    },
    description: {
      color: theme.color.grey.neutral400,
      fontSize: '15px',
      lineHeight: '21px',
    },
    progressText: {
      fontSize: 12,
      color: theme.color.grey.neutral300,
    },
  })
);

function RoleIcon({ icon }: { icon: string | null }) {
  const path = icon ? (iconList as Record<string, string>)[icon] : undefined;
  if (!path) {
    return null;
  }
  return <Icon path={path} size={1} color='white' />;
}

function descriptionState(description: string | null): EditorState | null {
  if (!description) return null;
  try {
    const state = EditorState.createWithContent(convertFromRaw(JSON.parse(description)));
    return state.getCurrentContent().hasText() ? state : null;
  } catch {
    return null;
  }
}

interface RoleTaskItemProps {
  title: string;
  isCompleted: boolean;
  onClick: () => void;
}

function RoleTaskItem({ title, isCompleted, onClick }: RoleTaskItemProps) {
  const { theme } = useCampfireTheme();
  const status = isCompleted ? 'completed' : 'new';
  const { cardBackgroundColor, textColor, iconColor, buttonBackgroundColor, buttonIconColor } = getStatusColors(
    status,
    theme
  );

  return (
    <Box
      onClick={onClick}
      display='flex'
      p='15px 20px'
      marginBottom='8px'
      justifyContent='space-between'
      alignItems='center'
      bgcolor={cardBackgroundColor}
      style={{ cursor: 'pointer', borderRadius: 6 }}
    >
      <Box display='inline-flex' alignItems='center' fontSize='1rem' fontWeight={600}>
        {isCompleted ? (
          <CheckCircle style={{ fontSize: 22, color: iconColor }} />
        ) : (
          <Cancel style={{ fontSize: 22, color: iconColor }} />
        )}
        <Box paddingLeft='10px'>
          <Typography style={{ color: textColor, fontWeight: 600 }}>{title}</Typography>
        </Box>
      </Box>
      <Box>
        <GoToButton background={buttonBackgroundColor} color={buttonIconColor} />
      </Box>
    </Box>
  );
}

interface Props {
  role: RoleSidebarRole;
  onClose: () => void;
  action?: React.ReactNode;
  statusFooter?: React.ReactNode;
  completedTaskIds: Set<string>;
  onTaskCompleted?: () => void;
}

export function RoleSidebar({ role, onClose, action, statusFooter, completedTaskIds, onTaskCompleted }: Props) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const editorState = descriptionState(role.description);
  const modeLabel = APPLICATION_MODE_LABEL.get(role.applicationMode);
  const tasks = role.tasks ?? [];
  const completedCount = tasks.filter((t) => completedTaskIds.has(t.taskId)).length;
  const [openTaskId, setOpenTaskId] = React.useState<string | undefined>(undefined);

  return (
    <Box position='relative' className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          <Box className={classes.iconBox}>
            <RoleIcon icon={role.icon} />
          </Box>
          <Typography className={`${classes.title} ${classes.titleText}`}>{role.name}</Typography>
        </Box>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <Close />
        </IconButton>
      </Box>

      <Box className={classes.metaRow}>
        {tasks.length > 0 && (
          <Box className={classes.metaCell}>
            <AssignmentTurnedInOutlined style={{ fontSize: 16, color: theme.color.grey.neutral200 }} />
            <Typography variant='caption' style={{ color: theme.color.grey.neutral200 }}>
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </Typography>
          </Box>
        )}
        {modeLabel && (
          <Box className={classes.metaCell}>
            <HowToRegOutlined style={{ fontSize: 16, color: theme.color.grey.neutral200 }} />
            <Typography variant='caption' style={{ color: theme.color.grey.neutral200 }}>
              {modeLabel}
            </Typography>
          </Box>
        )}
      </Box>

      {action && <Box mt={2}>{action}</Box>}
      {statusFooter && <Box mt={1}>{statusFooter}</Box>}

      {editorState && (
        <>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>About</Typography>
          </Box>
          <Box className={classes.description}>
            <CampfireRichEditor editorState={editorState} readOnly />
          </Box>
        </>
      )}

      {tasks.length > 0 && (
        <>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>Tasks</Typography>
            <Typography className={classes.progressText}>
              {completedCount} of {tasks.length} complete
            </Typography>
          </Box>
          {tasks.map((task) => (
            <RoleTaskItem
              key={task.taskId}
              title={task.title}
              isCompleted={completedTaskIds.has(task.taskId)}
              onClick={() => setOpenTaskId(task.taskId)}
            />
          ))}
        </>
      )}

      <TasksDialog
        open={openTaskId !== undefined}
        setOpen={(value) => {
          if (!value) setOpenTaskId(undefined);
        }}
        singleTaskId={openTaskId}
        onTaskCompleted={onTaskCompleted}
      />
    </Box>
  );
}
